import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const EMPTY_OVERVIEW = {
  stats: {
    total_accounts: 0,
    primary_tier_accounts: 0,
    additional_role_accounts: 0,
    inactive_accounts: 0,
  },
  partners: [],
};

async function readErrorMessage(response, fallbackMessage) {
  const raw = await response.text().catch(() => '');
  if (!raw) return fallbackMessage;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
      return parsed.detail.trim();
    }
    if (typeof parsed?.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch (_) {
    // fall through to raw text
  }

  return raw || fallbackMessage;
}

export function usePartnerAccountsState({
  adminFetch,
  endpoint,
  role,
  roleLabel,
}) {
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingIds, setSavingIds] = useState({});

  const fetchAccounts = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await adminFetch(endpoint);
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Failed to load ${roleLabel} accounts`));
      }

      const payload = await response.json().catch(() => ({}));
      setOverview({
        stats: {
          ...EMPTY_OVERVIEW.stats,
          ...(payload?.stats || {}),
        },
        partners: Array.isArray(payload?.partners) ? payload.partners : [],
      });
    } catch (error) {
      toast.error(error?.message || `Failed to load ${roleLabel} accounts`);
      setOverview(EMPTY_OVERVIEW);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminFetch, endpoint, roleLabel]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const updateRoles = useCallback(async (partner, nextPrimaryTier, nextAdditionalRoles, successMessage) => {
    if (!partner?.user_id) return;

    setSavingIds((current) => ({ ...current, [partner.user_id]: true }));
    try {
      const response = await adminFetch(`/admin/members/${partner.user_id}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_tier: nextPrimaryTier,
          additional_roles: nextAdditionalRoles,
        }),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Failed to update ${roleLabel} access`));
      }

      toast.success(successMessage);
      await fetchAccounts({ silent: true });
    } catch (error) {
      toast.error(error?.message || `Failed to update ${roleLabel} access`);
    } finally {
      setSavingIds((current) => {
        const next = { ...current };
        delete next[partner.user_id];
        return next;
      });
    }
  }, [adminFetch, fetchAccounts, roleLabel]);

  const promoteToPrimaryTier = useCallback(async (partner) => {
    if (!partner?.user_id) return;
    const nextAdditionalRoles = (partner.additional_roles || []).filter((item) => item !== role);
    await updateRoles(partner, role, nextAdditionalRoles, `${roleLabel} set as primary tier`);
  }, [role, roleLabel, updateRoles]);

  const removePartnerAccess = useCallback(async (partner) => {
    if (!partner?.user_id) return;
    const nextPrimaryTier = partner.tier === role ? 'member' : partner.tier || 'member';
    const nextAdditionalRoles = (partner.additional_roles || []).filter((item) => item !== role);
    await updateRoles(partner, nextPrimaryTier, nextAdditionalRoles, `${roleLabel} access removed`);
  }, [role, roleLabel, updateRoles]);

  return {
    overview,
    loading,
    refreshing,
    savingIds,
    fetchAccounts,
    promoteToPrimaryTier,
    removePartnerAccess,
  };
}
